/**
 * ROULETTE, SHARED MODE — the PURE half of the server-backed path (Phase E, plan §5.7,
 * 2026-09-06).
 *
 * Nothing in this file does I/O, touches the DOM, or imports React or the api client. It is
 * the arithmetic that turns the server's four round stamps into the six phases `Page.tsx`
 * already renders, plus the small state machine that decides whether a chip on the felt is
 * PENDING (posted, no ack), PLACED (acked) or UNPLACED (refused, or still un-acked when the
 * window shut). `sharedClock.ts` owns the network and calls in here; the Page renders what
 * comes out.
 *
 * WHY IT IS SPLIT OUT AT ALL. Two reasons, and the second is the one that matters:
 *   1. `scripts/assets/rlt_src.mjs` compiles ONE roulette module with esbuild and imports it,
 *      so a module that is to be unit-tested must not import a path alias
 *      (`@baaligames/shared-ui/...`) that esbuild cannot resolve. Every claim worth asserting
 *      about the shared path is arithmetic, and arithmetic lives here.
 *   2. The mapping below is the only place where a client decision could DISAGREE with the
 *      server's clock, and a disagreement is money: a board that stays live 200 ms past the
 *      server's cushion hands the player a 409 for a tap they made "in time". One file, one
 *      set of constants, one test.
 *
 * ── THE TIMELINE, AND WHY EACH BOUNDARY IS WHERE IT IS ────────────────────────────────────
 *
 * The server (shared/src/games/_tables/paces.js, PACES.roulette) runs one round as:
 *
 *     opened_at ──(window 20s / 12s quick)──> locks_at ──(arm 800ms)──> resolves_at
 *               ──(animation 7000ms)──> settles_at ──(intermission 7000ms)──> next_opens_at
 *
 * and `Page.tsx`'s shipped local round is:
 *
 *     betting ──> closing (Stop Betting) ──> arming (1400ms, peephole opens, nothing
 *             rotating) ──> spinning (T seconds of ball) ──> +250ms ──> the SEAT, then the
 *             seven-second result sequence (RESULT_HOLD 1200 / ZONES 1460..4460 /
 *             WIN_TEXT 4700 / COUNT_UP 4790 / ROUND_END 7000).
 *
 * The two fit together exactly, and that is not a coincidence — the local constants were
 * chosen from the same reference the server's paces were:
 *
 *   • BETTING ends at `locks_at − LOCK_CUSHION_MS`, not at `locks_at`. `model.placeBet`
 *     refuses inside the server's own 800 ms cushion, so a board that stayed live to the
 *     stroke of `locks_at` would take taps the server then throws `round_locked` at — a chip
 *     the player watched land and then watched vanish. The extra 150 ms is the plan's client
 *     margin (§5.3), covering the flight time of the request itself.
 *   • CLOSING runs from there to `resolves_at`: 950 + 800 = 1750 ms, which is the beat the
 *     local path spends on CLOSE_MS (2000 ms). "Stop Betting" is read where the player is
 *     already looking, before the camera moves.
 *   • ARMING is the local ARM_MS (1400 ms) measured from `resolves_at`, because the server's
 *     `resolving` window is where the wheel is allowed to move and nothing rotates during the
 *     arm anyway.
 *   • The FLIGHT is then whatever is left: `settles_at − resolves_at − ARM_MS − SEAT_PAD_MS`
 *     = 7000 − 1400 − 250 = 5350 ms, so the ball seats EXACTLY on `settles_at`. It is derived
 *     from the stamps rather than hardcoded, because `shared_tables.intermission_ms` (and,
 *     through `timelineFor`, the whole downstream timeline) is editable per table by an admin:
 *     a hardcoded 5.35 s would drift the moment somebody edited a row.
 *   • The RESULT SEQUENCE then owns `settles_at → next_opens_at`, which the server seeds at
 *     7000 ms — exactly ROUND_END_MS. The beats land where they always did.
 *
 * ── THE ONE RULE THAT OVERRIDES THE TIMELINE ─────────────────────────────────────────────
 * The wheel NEVER moves without the server's number. `resolves_at` may arrive before the poll
 * that carries the result does (the reveal gate opens at `resolving`, and a poll is up to
 * ~400 ms + RTT behind), and a client that started the animation on the stamp alone would have
 * to invent a pocket to aim at. So `phaseAt` stalls in `closing` until `hasNumber` is true.
 * That stall is invisible in the normal case, because the first 1400 ms of `resolving` is the
 * arm and nothing is rotating; it is only visible when the worker is genuinely late, which is
 * exactly when the player should be told the table is syncing rather than shown a lie.
 */

import { type BetKind } from './engine';

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Constants that MUST agree with the server
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** `paces.js` PACES.roulette.lock — `/bet` refuses inside this cushion before `locks_at`. */
export const LOCK_MS = 800;
/** …plus the plan's 150 ms client margin (§5.3). The board shuts here, not at `locks_at`. */
export const LOCK_CUSHION_MS = LOCK_MS + 150;
/** `paces.js` PACES.roulette.arm — `locks_at` → `resolves_at`. Informational here; the phase
 *  boundary is the server's own `resolvesAt` stamp, never this number. */
export const SERVER_ARM_MS = 800;

/** Page.tsx's ARM_MS: the peephole opens and the wheel revs, with NOTHING rotating. */
export const ARM_MS = 1400;
/** Page.tsx's `plan.T * 1000 + 250` — the hold between the ball stopping and the seat beat. */
export const SEAT_PAD_MS = 250;
/** A ball that flies for less than this reads as a glitch, so a mis-seeded table gets a late
 *  seat rather than a two-frame spin. Only reachable if an admin sets an animation window
 *  shorter than ARM_MS + this + SEAT_PAD_MS (4150 ms); the seeded 7000 is nowhere near it. */
export const MIN_FLIGHT_MS = 2_500;

/** Page.tsx's result sequence, repeated here ONLY as the fallback when the server did not
 *  give us a `nextOpensAt` (a round read before the worker stamped it). */
export const RESULT_HOLD_MS = 1_200;
export const ROUND_END_MS = 7_000;

/** How long past `resolves_at` we wait for the number before saying we are out of sync. */
export const SYNCING_AFTER_MS = 1_000;

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Types
 * ════════════════════════════════════════════════════════════════════════════════════════ */

export type SrvStatus = 'pending' | 'betting' | 'locked' | 'resolving' | 'settling' | 'settled' | 'void';

/** The six phases `Page.tsx` already renders, plus `idle` for "no round to show yet". */
export type ClientPhase = 'betting' | 'closing' | 'arming' | 'spinning' | 'result' | 'reveal';

/**
 * One round, as the clock hands it to the Page. Every `*At` is in the `performance.now()`
 * domain — converted once, on the way in, by the clock's `toPerf` — because that is the domain
 * every shipped line of `Page.tsx` and `Wheel.tsx` subtracts from.
 */
export type SharedRound = {
  id: number;
  no: number;
  status: SrvStatus;
  opensAt: number;
  locksAt: number;
  resolvesAt: number | null;
  settlesAt: number | null;
  nextOpensAt: number | null;
  /** the winning pocket, or null until the server reveals it at `resolving` */
  n: number | null;
  pot: number;
  bettors: number;
};

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The timeline
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** The instant the board must stop taking chips — see LOCK_CUSHION_MS. */
export function betClosesAt(r: Pick<SharedRound, 'locksAt'>): number {
  return r.locksAt - LOCK_CUSHION_MS;
}

/**
 * The ball's flight, in SECONDS, so that it seats exactly on `settlesAt`.
 *
 * Derived from the round's own stamps, never from a constant: `settles_at − resolves_at` is
 * `paces.animationMs('roulette')` today (7000 ms) but it is written into the row by the
 * worker, and a table an admin has edited must animate to ITS clock, not to this file's idea
 * of one.
 */
export function flightSecondsFor(resolvesAt: number, settlesAt: number): number {
  const anim = Math.max(0, settlesAt - resolvesAt);
  const flight = Math.max(MIN_FLIGHT_MS, anim - ARM_MS - SEAT_PAD_MS);
  return flight / 1000;
}

/** The instant the ball seats — the `t = 0` of the whole result sequence. */
export function seatAtFor(resolvesAt: number, settlesAt: number): number {
  return resolvesAt + ARM_MS + flightSecondsFor(resolvesAt, settlesAt) * 1000 + SEAT_PAD_MS;
}

/**
 * The phase to render, from the server's stamps and nothing else.
 *
 * `hasNumber` is the server's reveal, NOT a guess: `_tables/model.js` publishes
 * `round.result` only from `resolving` onward for roulette (REVEAL_STATUSES), so this is false
 * for the whole betting window and the whole arm — and while it is false the wheel may not
 * move. See the header.
 */
export function phaseAt(r: SharedRound, now: number, hasNumber: boolean): ClientPhase {
  if (now < betClosesAt(r)) return 'betting';
  const resolvesAt = r.resolvesAt;
  // No `resolves_at` yet (a `pending`/`betting` row the worker has not decided) — the round is
  // shut but nothing may spin.
  if (resolvesAt == null || now < resolvesAt) return 'closing';
  // The stall. A number we do not have is a number we do not animate.
  if (!hasNumber) return 'closing';
  const settlesAt = r.settlesAt ?? (resolvesAt + ARM_MS + MIN_FLIGHT_MS + SEAT_PAD_MS);
  if (now < resolvesAt + ARM_MS) return 'arming';
  const seatAt = seatAtFor(resolvesAt, settlesAt);
  if (now < seatAt) return 'spinning';
  if (now < seatAt + RESULT_HOLD_MS) return 'result';
  return 'reveal';
}

/** True when the wheel is overdue: past `resolves_at + 1 s` with no number from the server. */
export function syncingAt(r: SharedRound, now: number, hasNumber: boolean): boolean {
  if (hasNumber) return false;
  const resolvesAt = r.resolvesAt;
  if (resolvesAt == null) return false;
  return now > resolvesAt + SYNCING_AFTER_MS;
}

/**
 * The countdown, in whole seconds, that the player sees.
 *
 * Counted to `betClosesAt`, not to `locks_at`, so the number on screen is the number of
 * seconds the board will actually accept a chip for. A countdown that reads "1" over a dead
 * board is the same lie as a live board that refuses taps.
 */
export function secsLeftAt(r: SharedRound, now: number): number {
  return Math.max(0, Math.ceil((betClosesAt(r) - now) / 1000));
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Chips and their acks
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * One PLACEMENT — one tap (or one debounced burst of taps on the same position), which is one
 * `shared_round_bets` row once the server has taken it.
 *
 * `queued` is the 250 ms debounce window: the chip is on the felt and the money is held
 * optimistically, but no request has left yet. `pending` is in flight. `placed` is acked.
 * `rejected` is a refusal — the money never left and the chip is owed back to the player, as
 * a NOT PLACED chip that pre-fills the next round.
 */
export type PlacementState = 'queued' | 'pending' | 'placed' | 'rejected';

export type Placement = {
  /** the idempotency key this placement was bought with — one per tap, forever */
  key: string;
  /** the board position id (`Placed.id`), so several placements can stack into one chip */
  posId: string;
  kind: BetKind;
  numbers: number[];
  amount: number;
  state: PlacementState;
  roundNo: number;
  roundId: number | null;
  /** the real `shared_round_bets.id`, null until the ack lands */
  betId: number | null;
};

/** What a chip on the felt looks like: solid only when EVERY rupee under it is acked. */
export type AckState = 'pending' | 'placed' | 'unplaced';

/**
 * THE CHIP ON ONE BOARD POSITION: what it says on its face, and how it paints.
 *
 * The face must always be the money AT RISK, and that is the whole reason this is not simply
 * "sum the amounts". A position can hold a mix — ₹100 acked, ₹200 refused at the lock — and
 * printing ₹300 on it would be a lie about the player's own money in the one direction that
 * matters, since the ₹200 is already back in their wallet.
 *
 *   • Any live rupees (acked, or in flight) → the face shows THOSE, and the chip is solid if
 *     every one of them is acked and translucent if any is still travelling. Rupees that were
 *     refused are reported by the NOT PLACED strip, not by the chip.
 *   • No live rupees at all, but something was refused → the chip STAYS on the felt, greyed,
 *     showing what did not go on. §5.7 asks for exactly this ("any unacked chip greys out"),
 *     and it is the honest shape: a chip that silently vanishes is a chip the player will
 *     swear they placed.
 *   • Nothing at all → no chip.
 */
export function chipOf(placements: readonly Placement[]): { amount: number; ack: AckState } | null {
  let placed = 0; let pending = 0; let rejected = 0;
  for (const p of placements) {
    if (p.state === 'placed') placed += p.amount;
    else if (p.state === 'rejected') rejected += p.amount;
    else pending += p.amount;                      // 'queued' (in the debounce) or 'pending'
  }
  const live = placed + pending;
  if (live > 0) return { amount: live, ack: pending > 0 ? 'pending' : 'placed' };
  if (rejected > 0) return { amount: rejected, ack: 'unplaced' };
  return null;
}

/** Just the paint state — see `chipOf`. A position with nothing on it paints as placed, which
 *  is what the shipped local path does (it has no ack concept at all). */
export function ackStateOf(placements: readonly Placement[]): AckState {
  return chipOf(placements)?.ack ?? 'placed';
}

/** The rupees on a position that the server has actually taken. */
export function placedAmount(placements: readonly Placement[]): number {
  let n = 0;
  for (const p of placements) if (p.state === 'placed') n += p.amount;
  return n;
}

/** The rupees on a position that never made it — the NOT PLACED total. */
export function unplacedAmount(placements: readonly Placement[]): number {
  let n = 0;
  for (const p of placements) if (p.state === 'rejected') n += p.amount;
  return n;
}

/**
 * At the lock, everything that is not `placed` is NOT PLACED.
 *
 * §5.7: "at lock any unacked chip greys out, the strip says NOT PLACED — KEPT FOR NEXT SPIN,
 * and those chips pre-fill the next round's slip." This is the transition that implements the
 * first half; the second half is the caller keeping what comes back out of `carryOverOf`.
 *
 * Note that a `pending` placement is condemned here even though its request may still be in
 * flight and may still succeed. That is deliberate and it is the safe direction: the ack path
 * re-promotes a placement that lands after all (`state: 'placed'`), and the carry-over list is
 * only an OFFER of chips for the next round — the player still has to leave them there. The
 * opposite bias, waiting to see, would leave a chip looking alive on a dead round.
 */
export function condemnUnacked(placements: readonly Placement[]): Placement[] {
  return placements.map((p) => (p.state === 'placed' ? p : { ...p, state: 'rejected' as const }));
}

/** The chips to offer back on the next round, merged per position and in tap order. */
export function carryOverOf(placements: readonly Placement[]): {
  posId: string; kind: BetKind; numbers: number[]; amount: number;
}[] {
  const out: { posId: string; kind: BetKind; numbers: number[]; amount: number }[] = [];
  const byId = new Map<string, number>();
  for (const p of placements) {
    if (p.state !== 'rejected') continue;
    const i = byId.get(p.posId);
    if (i == null) {
      byId.set(p.posId, out.length);
      out.push({ posId: p.posId, kind: p.kind, numbers: [...p.numbers], amount: p.amount });
    } else {
      out[i].amount += p.amount;
    }
  }
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The debounce
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * §5.7's 250 ms: "a burst of taps becomes one request carrying several bets".
 *
 * WHAT THE SERVICE CAN ACTUALLY TAKE. `_tables/routes.js` `POST /bet` is a SINGLE placement —
 * one `{market, selection, amount, idempotencyKey}` per request — and reshaping the shared
 * service for roulette is out of bounds (it is verified, shipped money code for four other
 * games). So the debounce delivers the intent the only way it can without touching the
 * service: taps landing inside one 250 ms window are MERGED PER POSITION into a single
 * placement each, and the whole merged batch is flushed together. Five taps on RED become one
 * request for ₹250 instead of five for ₹50; five taps on five different numbers still cost
 * five requests, because five different numbers are five different bets and the wire has no
 * way to say otherwise.
 *
 * This is the shape that matters for the player: chip-stacking (the overwhelmingly common
 * burst) collapses, and the felt shows ONE chip for it — which is what `Page.tsx` already
 * draws, since it merges repeat placements on a position into one `Placed` row.
 */
export const DEBOUNCE_MS = 250;

/** One position's worth of a pending burst. */
export type PendingPlacement = { posId: string; kind: BetKind; numbers: number[]; amount: number };

/**
 * Merge a new tap into a burst. Pure, so the merge rule is testable without a timer.
 *
 * Order is preserved (a position keeps the slot of its FIRST tap in the burst) so the requests
 * leave in the order the player tapped, which is the order they will read back in support.
 */
export function mergeBurst(
  burst: readonly PendingPlacement[],
  tap: PendingPlacement,
): PendingPlacement[] {
  const out = burst.map((b) => ({ ...b, numbers: [...b.numbers] }));
  const i = out.findIndex((b) => b.posId === tap.posId);
  if (i >= 0) { out[i] = { ...out[i], amount: out[i].amount + tap.amount }; return out; }
  out.push({ ...tap, numbers: [...tap.numbers] });
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Settlement, provisionally
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * What this round did to me, derived from the server's REVEALED number over my ACKED chips.
 *
 * Used only in the gap between the ball seating and `shared_user_ledger` landing — a gap of a
 * second or two while the worker settles the table. Without it a winner reads "No win this
 * round" for that second, which is the worst possible moment to be wrong. The instant the
 * server's own ledger row arrives it is preferred, always (the clock's `settledOutcome`).
 *
 * The arithmetic is the settle transaction's own: `floor(stake × (PAYOUT + 1))` PER CHIP,
 * never a sum floored once — the same per-chip floor `adapter.settleFor` runs, which is why
 * the two agree to the rupee. Only `placed` placements count: a chip the server refused is not
 * at risk and cannot win.
 */
export function provisionalOutcome(
  placements: readonly Placement[],
  n: number,
  returnOf: (kind: BetKind, amount: number) => number,
): { staked: number; returned: number; net: number } | null {
  let staked = 0; let returned = 0; let any = false;
  for (const p of placements) {
    if (p.state !== 'placed') continue;
    any = true;
    staked += p.amount;
    // FLOORED HERE, not by the caller. `lattice.returnOf` is `stake × (PAYOUT + 1)` unrounded
    // — which happens to be exact today, because every PAYOUT is a whole number (36/covered −
    // 1, with the owner's 29 for a straight) — while `engine.betReturn` on BOTH sides of the
    // wire floors. Doing it here means the day somebody adds a fractional payout, the client's
    // provisional figure follows the settle transaction instead of drifting a rupee above it.
    if (p.numbers.includes(n)) returned += Math.floor(returnOf(p.kind, p.amount) + 1e-9);
  }
  if (!any) return null;
  return { staked, returned, net: returned - staked };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The lobby
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * A table's occupancy: the REAL bettors on its live round plus the fake seats §5.7 keeps.
 *
 * The owner's brief (O3) is explicit that the mock crowd stays — "keep the mock users + their
 * betting theatre" — so a shared table shows what it really has PLUS the texture, and never
 * fewer people than are genuinely sitting there. The fake half is a pure function of the
 * SERVER's `fake_seed` and round number, so two phones at one table agree (plan §5.4); the
 * device seed that drove the local lobby is not consulted at all.
 */
export function occupancyOf(fakeSeed: number, roundNo: number, seatBase: number, realBettors: number): number {
  const idx = Math.max(0, Math.floor(roundNo));
  const slow = Math.sin((idx / 140) + (fakeSeed % 1000) / 159.15) * 0.22;
  const jitter = (hash32(fakeSeed, idx) / 0x100000000 - 0.5) * 0.10;
  const fake = Math.max(6, Math.round(seatBase * (1 + slow + jitter)));
  return fake + Math.max(0, Math.floor(realBettors));
}

/** `tables.ts`'s hash, duplicated rather than imported so this module stays dependency-free
 *  for the esbuild-one-module test loader. Identical function, asserted by the client test. */
export function hash32(a: number, b: number): number {
  let h = (a ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (b + 0x165667b1), 0x27d4eb2d) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** A stable, human-readable id for a SERVER table — same alphabet as the local lobby's, but a
 *  pure function of the table's own row id, so every device at Table 2 reads the same code. */
const B32 = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
export function shortIdFor(tableId: number, fakeSeed: number): string {
  let s = '';
  for (let i = 0; i < 6; i++) s += B32[hash32((tableId * 2654435761) >>> 0, fakeSeed + i + 101) % B32.length];
  return `${s.slice(0, 4)}-${s.slice(4)}`;
}

/** The seat base a table is drawn around — derived from its server seed, never from the device. */
export function seatBaseFor(fakeSeed: number): number {
  return 40 + (hash32(fakeSeed, 11) % 620);
}
